import { useCallback, useEffect, useRef, useState } from 'react';

/** Minimum time the pressed visual stays visible (ms). */
export const APP_PRESS_MIN_MS = 280;

/**
 * Keeps `data-pressed` active long enough for the scale/background animation to read clearly.
 */
export function useButtonPress({ disabled = false, loading = false } = {}) {
  const [pressed, setPressed] = useState(false);
  const pressStartRef = useRef(0);
  const releaseTimerRef = useRef(null);

  const clearReleaseTimer = useCallback(() => {
    if (releaseTimerRef.current != null) {
      clearTimeout(releaseTimerRef.current);
      releaseTimerRef.current = null;
    }
  }, []);

  const scheduleRelease = useCallback(() => {
    if (!pressStartRef.current) return;
    const elapsed = Date.now() - pressStartRef.current;
    const remaining = Math.max(0, APP_PRESS_MIN_MS - elapsed);
    clearReleaseTimer();
    releaseTimerRef.current = setTimeout(() => {
      setPressed(false);
      pressStartRef.current = 0;
      releaseTimerRef.current = null;
    }, remaining);
  }, [clearReleaseTimer]);

  const onPointerDown = useCallback(
    (event) => {
      if (disabled || loading || (event.button != null && event.button !== 0)) return;
      clearReleaseTimer();
      pressStartRef.current = Date.now();
      setPressed(true);
    },
    [disabled, loading, clearReleaseTimer]
  );

  const onPointerUp = useCallback(() => {
    scheduleRelease();
  }, [scheduleRelease]);

  const onPointerLeave = onPointerUp;
  const onPointerCancel = onPointerUp;

  useEffect(() => () => clearReleaseTimer(), [clearReleaseTimer]);

  return {
    pressed,
    pressHandlers: { onPointerDown, onPointerUp, onPointerLeave, onPointerCancel },
  };
}
